import { Avatar, Button, Icon, Spinner } from "@deligoo/ui"
import { unwrapResult } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { selectDriverById } from "@/store/drivers"
import { requestAssignOrder, selectOrderById } from "@/store/orders"
import { setAboutPanel, setShowDriverDetails } from "@/store/ui"
import { Driver, Order } from "@/types"
import { DriverUtils, OrderUtils } from "@/utils"

import { showConfirmModal } from "../ConfirmModal"
import cls from "./DriverHeader.module.scss"

type DriverHeaderProps = {
  driverId?: Driver["id"] | null
  orderId: Order["id"]
  subheader?: React.ReactNode
  action?: React.ReactNode
}

export const DriverHeader = ({ driverId, subheader, action, orderId }: DriverHeaderProps) => {
  const driverInState = useAppSelector(driverId ? selectDriverById(driverId) : () => null, shallowEqual)
  const order = useAppSelector(selectOrderById(orderId))

  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  async function assignOrder(orderId: Order["id"]) {
    const res = await dispatch(requestAssignOrder(orderId)).then(unwrapResult)

    if (res.ok) toast.success(t("orders:assign_order_success"))
  }

  function handleDriverClick() {
    if (driverInState) {
      dispatch(setShowDriverDetails(driverInState.id))
    } else {
      showConfirmModal({
        title: "Przypisanie zlecenia",
        description: "Czy chcesz przypisać to zlecenie?",
        handleConfirm: () => assignOrder(orderId),
        autoFocusConfirm: true,
      })
    }
  }

  if (!order) return null

  if (driverId && !driverInState && order.driver) return <DriverReassignedPlaceholder driver={order.driver} />
  if (driverId && !driverInState) return <DriverPlaceholder />

  return (
    <div className={cls.driverHeader}>
      <button
        className={cls.driver}
        onClick={handleDriverClick}
        disabled={!driverInState && !OrderUtils.canAssignOrder(order)}
      >
        {driverInState ? (
          <Avatar
            color={DriverUtils.getAvatarColor(driverInState)}
            indicator={DriverUtils.getAvatarIndicator(driverInState)}
            dotColor={DriverUtils.getAvatarDotColor(driverInState)}
            className={cls.avatar}
          >
            {driverInState.name}
          </Avatar>
        ) : (
          <Avatar className={cls.avatar}>+</Avatar>
        )}
        <div>
          <div className={cls.name}>{driverInState ? driverInState.name : t("assign_driver")}</div>
          {driverInState && subheader && <div className="mt-1">{subheader}</div>}
        </div>
      </button>

      {Boolean(action) && (
        <div className={cls.action}>
          {orderId && (
            <Button
              size="small"
              variation="text"
              className="mx-1.5"
              onClick={() => {
                dispatch(setShowDriverDetails(driverInState ? driverInState.id : false))
                dispatch(setAboutPanel({ type: "order", id: orderId }))
              }}
            >
              <Icon name="crosshair" color="primary" />
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  )
}

const DriverReassignedPlaceholder = ({ driver }: { driver: Order["driver"] }) => {
  const { t } = useTranslation()

  function handleReassignedDriverClick() {
    toast.info(`${t("orders:courier_reassigned_to_team")} ${driver?.team.name}`)
  }

  if (!driver) return null

  return (
    <div className={cls.driverHeader}>
      <button className={cls.driver} onClick={handleReassignedDriverClick}>
        <Avatar className={cls.avatar}>{driver.name}</Avatar>
        {driver.name}
      </button>
    </div>
  )
}

const DriverPlaceholder = () => {
  return (
    <div className={cls.driverHeader}>
      <Avatar className={cls.avatar} />
      <Spinner size="small" />
    </div>
  )
}
